import { useEffect, useMemo, useRef, useState } from 'react';

import {
  getMySessionFilterOptions,
  getSessionFilterOptions,
  type SessionFilterOptions,
  type SessionQuery,
} from '../api/sessions';
import type { RecordingFilter } from './recordingFilterRules';

/**
 * The bridge between the filter bar and the server.
 *
 * The roster endpoint returns the 200 most recent sessions. Filtering in the
 * browser therefore filtered *what the cap had already kept*, which is only
 * the same thing while a tenant has fewer sessions than the cap. Measured on
 * dev: 182 active sessions, and 275 once disabled recordings are shown — so
 * the "Show disabled" view is already truncated by 75 today, and a filter
 * over the returned page cannot reach them. It answers "nothing found" rather
 * than "that is out of reach", which is the failure mode where the tool lies
 * quietly.
 *
 * Two halves have to move, not one. Sending the filter to the server fixes
 * the ROWS; without also fetching the option lists the CONTROLS still only
 * offer what survived the cap, and the participant you most need to search
 * for is exactly the one whose recordings fell off the end.
 */

/** Participant label → user id, for turning a filter into a query. */
export type ParticipantIds = Record<string, string>;

/**
 * Convert the UI filter into query params.
 *
 * Participants go over the wire as ids, not names: a rename must not silently
 * change what a shared link returns. The filter itself stays label-based —
 * labels are what the URL carries and what a human reading the link
 * understands — so the mapping happens here, at the edge.
 *
 * A label with no known id is dropped from the query rather than sent as a
 * name the server would not match. The client-side pass still applies it, so
 * the result narrows correctly either way; it just can't reach past the cap
 * for that one name. Silently widening to "everyone" would be the worse
 * failure.
 */
export function toSessionQuery(
  f: RecordingFilter,
  ids: ParticipantIds = {},
): SessionQuery {
  return {
    participantIds: f.participants
      .map((label) => ids[label])
      .filter((id): id is string => !!id),
    procedures: f.procedures,
    devices: f.devices,
    startedFrom: f.from || undefined,
    startedTo: f.to || undefined,
    processingFailed: f.processingFailed,
  };
}

/**
 * Hold a value still until it stops changing.
 *
 * The dropdowns emit one change per click, but the exact-range inputs emit one
 * per edit — typing a date would otherwise fire a request per keystroke, and
 * the responses could land out of order.
 */
export function useDebounced<T>(value: T, ms = 300): T {
  const [held, setHeld] = useState(value);
  // Compared by value, so a filter object rebuilt each render doesn't reset
  // the timer forever.
  const key = JSON.stringify(value);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const t = setTimeout(() => setHeld(value), ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ms]);
  return held;
}

const NO_OPTIONS: SessionFilterOptions = {
  participants: [],
  procedures: [],
  devices: [],
};

export interface FilterSource {
  /** Label lists, in the shape `RecordingFilters` takes. */
  options: { participants: string[]; devices: string[]; procedures: string[] };
  /** For `toSessionQuery`. */
  participantIds: ParticipantIds;
  /** False while the first fetch is in flight, or if it failed. */
  ready: boolean;
}

/**
 * The values to offer in the filter bar, across the tenant's whole history.
 *
 * On failure this returns empty lists rather than throwing: a filter bar with
 * nothing to choose from is a degraded screen, but a dashboard that won't
 * render because its dropdowns couldn't populate is a broken one.
 */
export function useSessionFilterOptions(
  tenantId: string | null | undefined,
  includeDeleted = false,
): FilterSource {
  const [data, setData] = useState<SessionFilterOptions>(NO_OPTIONS);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setReady(false);
    getSessionFilterOptions(tenantId, includeDeleted)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setData(NO_OPTIONS);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, includeDeleted]);

  return useMemo(() => {
    const participantIds: ParticipantIds = {};
    for (const p of data.participants) if (p.id) participantIds[p.label] = p.id;
    return {
      options: {
        participants: data.participants.map((p) => p.label),
        devices: data.devices,
        procedures: data.procedures,
      },
      participantIds,
      ready,
    };
  }, [data, ready]);
}

/**
 * Union two option sets.
 *
 * The server knows the session history; it does not know about baselines,
 * which some screens list in the same timeline. Taking only the server's
 * lists would drop a participant who has a baseline but no captures, and
 * taking only the rows' would re-introduce the cap. Both, deduped.
 */
export function mergeOptions(
  a: FilterSource['options'],
  b: FilterSource['options'],
): FilterSource['options'] {
  const union = (x: string[], y: string[]) =>
    [...new Set([...x, ...y])].sort((m, n) =>
      m.localeCompare(n, undefined, { sensitivity: 'base' }),
    );
  return {
    participants: union(a.participants, b.participants),
    devices: union(a.devices, b.devices),
    procedures: union(a.procedures, b.procedures),
  };
}

/**
 * The same thing for a participant looking at their own captures.
 *
 * A participant can't call the tenant-admin options route, and their own
 * history spans every tenant they record in, so this is a separate endpoint
 * rather than the tenant one with a narrower scope. No participants list:
 * there is exactly one, and it is them.
 */
export function useMySessionFilterOptions(): FilterSource {
  const [data, setData] = useState<{ procedures: string[]; devices: string[] }>(
    { procedures: [], devices: [] },
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMySessionFilterOptions()
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(
    () => ({
      options: { participants: [], devices: data.devices, procedures: data.procedures },
      participantIds: {},
      ready,
    }),
    [data, ready],
  );
}
